import { useEffect, useRef, useState } from "react";

import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useImage } from "@/features/request/feedback-tool/feedbackTool/image/hooks/useImage.tsx";
import {
  CONTROLS_DEFAULT,
  CONTROLS_MIN,
  CONTROLS_STEP,
  getBaseZoom,
  getPinchDistance,
  ZOOM_STEP,
} from "@/features/request/feedback-tool/feedbackTool/utils";

import { useCanAddMoreFeedbacks } from "../../hooks/useCanAddMoreFeedbacks";

export function Image({
  imgRef,
}: {
  imgRef: React.MutableRefObject<HTMLImageElement | null>;
}) {
  const {
    applyZoom,
    controls,
    divImgRef,
    feedback,
    img,
    isTablet,
    selected,
    setFormFeedBack,
    setSelected,
    size,
    zoom,
  } = useFeedbackTool();

  const isPinchActiveRef = useRef(false);
  const { animationZoom } = useImage(imgRef, isPinchActiveRef);

  const { hasRole } = useRoleAccess();
  const isDesigner = hasRole(["designer"]);
  const canNotAddFeedback = useCanAddMoreFeedbacks();
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({
    scrollLeft: 0,
    scrollTop: 0,
    x: 0,
    y: 0,
  });

  const zoomRef = useRef(zoom);
  const controlsRef = useRef(controls);
  const sizeRef = useRef(size);
  const applyZoomRef = useRef(applyZoom);
  const pinchStartRef = useRef<{
    controls: number;
    distance: number;
    height: number;
    imgNormX: number;
    imgNormY: number;
    scrollLeft: number;
    scrollTop: number;
    zoom: number;
  } | null>(null);
  const touchDragStartRef = useRef<{
    scrollLeft: number;
    scrollTop: number;
    x: number;
    y: number;
  } | null>(null);
  const latestPinchRef = useRef<{ controls: number; zoom: number } | null>(
    null,
  );

  zoomRef.current = zoom;
  controlsRef.current = controls;
  sizeRef.current = size;
  applyZoomRef.current = applyZoom;

  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (size === "MODAL") return;
    if (event.deltaY < 0) {
      if (controls <= CONTROLS_MIN) return;
      applyZoom(zoom + ZOOM_STEP, controls - CONTROLS_STEP);
    } else {
      if (controls >= CONTROLS_DEFAULT) return;
      applyZoom(zoom - ZOOM_STEP, controls + CONTROLS_STEP);
    }
  };

  const handleImageClick = (event: React.MouseEvent) => {
    if (isDesigner || canNotAddFeedback) return;
    const imgR = imgRef.current;
    const container = divImgRef.current;

    if (!imgR || !container) return;
    if (selected) return setSelected(null);

    const rect = imgRef.current?.getBoundingClientRect();

    if (!rect) return;

    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;

    setFormFeedBack({
      description: "",
      format: "IMAGE",
      id: feedback ? feedback.length + 1 : 1,
      imgId: img.id,
      storyId: 0,
      type: "ADD",
      user: {
        id: 0,
        name: "",
      },
      x,
      y,
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = divImgRef.current;

    if (!container) return;

    setIsDragging(true);
    setStartPos({
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const container = divImgRef.current;

    if (!container) return;

    const dx = e.clientX - startPos.x;
    const dy = e.clientY - startPos.y;

    container.scrollLeft = startPos.scrollLeft - dx;
    container.scrollTop = startPos.scrollTop - dy;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const container = divImgRef.current;

    if (!container) return;

    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseUp);

    return () => {
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseUp);
    };
  }, [imgRef.current, divImgRef.current]);

  useEffect(() => {
    const container = divImgRef.current;

    if (!container) return;

    // Touches inside the feedback balloon must not pan the container —
    // panning under the finger cancels react-aria presses (X, Add Feedback).
    const isInsideBalloon = (e: TouchEvent) =>
      e.target instanceof Element &&
      e.target.closest('[data-name="box-image"]');

    const onTouchStart = (e: TouchEvent) => {
      const imgEl = imgRef.current;

      if (!imgEl || isInsideBalloon(e)) return;

      if (e.touches.length === 2) {
        isPinchActiveRef.current = true;

        const t0 = e.touches.item(0)!;
        const t1 = e.touches.item(1)!;
        const midX = (t0.clientX + t1.clientX) / 2;
        const midY = (t0.clientY + t1.clientY) / 2;
        const imgRect = imgEl.getBoundingClientRect();

        pinchStartRef.current = {
          controls: controlsRef.current,
          distance: getPinchDistance(e.touches),
          height: imgEl.clientHeight,
          imgNormX: (midX - imgRect.left) / imgRect.width,
          imgNormY: (midY - imgRect.top) / imgRect.height,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
          zoom: imgEl.clientWidth,
        };
        imgEl.style.maxWidth = "none";
        imgEl.style.maxHeight = "none";
        touchDragStartRef.current = null;
      } else if (e.touches.length === 1) {
        const touch = e.touches.item(0);

        if (!touch) return;

        touchDragStartRef.current = {
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop,
          x: touch.clientX,
          y: touch.clientY,
        };
        pinchStartRef.current = null;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isInsideBalloon(e)) return;

      const ps = pinchStartRef.current;
      const tds = touchDragStartRef.current;
      const imgEl = imgRef.current;

      if (e.touches.length === 2 && ps && imgEl) {
        e.preventDefault();
        const newDistance = getPinchDistance(e.touches);
        const ratio = newDistance / ps.distance;
        const minZoom =
          ps.zoom -
          (CONTROLS_DEFAULT - ps.controls) * (ZOOM_STEP / CONTROLS_STEP);
        const maxZoom =
          ps.zoom + (ps.controls - CONTROLS_MIN) * (ZOOM_STEP / CONTROLS_STEP);
        const newZoom = Math.max(minZoom, Math.min(maxZoom, ps.zoom * ratio));
        const newControls = Math.max(
          CONTROLS_MIN,
          Math.min(
            CONTROLS_DEFAULT,
            ps.controls - (newZoom - ps.zoom) * (CONTROLS_STEP / ZOOM_STEP),
          ),
        );
        const newHeight = ps.height * (newZoom / ps.zoom);

        imgEl.style.width = `${newZoom}px`;
        container.scrollLeft =
          ps.scrollLeft + ps.imgNormX * (newZoom - ps.zoom);
        container.scrollTop =
          ps.scrollTop + ps.imgNormY * (newHeight - ps.height);
        latestPinchRef.current = { controls: newControls, zoom: newZoom };
      } else if (e.touches.length === 1 && tds) {
        const touch = e.touches.item(0);

        if (!touch) return;

        container.scrollLeft = tds.scrollLeft - (touch.clientX - tds.x);
        container.scrollTop = tds.scrollTop - (touch.clientY - tds.y);
      }
    };

    const onTouchEnd = () => {
      isPinchActiveRef.current = false;

      const latest = latestPinchRef.current;

      if (latest) {
        applyZoomRef.current(latest.zoom, latest.controls);
        latestPinchRef.current = null;
      }

      pinchStartRef.current = null;
      touchDragStartRef.current = null;
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const baseZoom = getBaseZoom(
        sizeRef.current,
        controlsRef.current,
        imgRef.current,
        zoomRef.current,
      );
      const factor = event.deltaY < 0 ? 1.03 : 1 / 1.03;
      const fitZoom =
        baseZoom -
        (CONTROLS_DEFAULT - controlsRef.current) * (ZOOM_STEP / CONTROLS_STEP);
      const newZoom = Math.max(
        fitZoom,
        Math.min(fitZoom + 1200, baseZoom * factor),
      );
      const newControls = Math.max(
        CONTROLS_MIN,
        Math.min(
          CONTROLS_DEFAULT,
          controlsRef.current -
            (newZoom - baseZoom) * (CONTROLS_STEP / ZOOM_STEP),
        ),
      );

      applyZoomRef.current(newZoom, newControls);
    };

    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd, { passive: true });
    if (isTablet) {
      container.addEventListener("wheel", onWheel, { passive: false });
    }

    return () => {
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      if (isTablet) {
        container.removeEventListener("wheel", onWheel);
      }
    };
  }, [divImgRef.current]);

  const maxDimension =
    size === "FULL"
      ? controls === CONTROLS_DEFAULT
        ? "100%"
        : "none"
      : controls < CONTROLS_DEFAULT
        ? "none"
        : "";

  return (
    <img
      className={`max-w-full ${size === "MODAL" && controls === CONTROLS_DEFAULT ? "max-w-150 max-h-150" : "transition-transform duration-300 ease-in-out"} ${isDragging ? "cursor-grabbing" : "cursor-crosshair"}`}
      onClick={handleImageClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onWheel={isTablet ? undefined : handleWheel}
      ref={imgRef}
      src={img.url}
      style={{
        maxHeight: maxDimension,
        maxWidth: maxDimension,
        objectFit: "contain",
        transition: animationZoom,
        width: size === "FULL" || controls < CONTROLS_DEFAULT ? zoom : "auto",
      }}
    />
  );
}
